<template>
  <div class="registro-tareas-container p-3 sm:p-4">
    <!-- Top Navigation Row (Encima de todo) -->
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <vs-button
        icon="arrow_back"
        size="small"
        type="border"
        color="dark"
        class="font-semibold shadow-xs"
        @click="$router.push('/actividades-control')"
      >
        Regresar al Control
      </vs-button>

      <div v-if="nombreDepartamento" class="dept-badge">
        <i class="feather icon-briefcase text-primary mr-1"></i>
        <span>Área: <strong>{{ nombreDepartamento }}</strong></span>
      </div>
    </div>

    <!-- Hero Banner -->
    <div class="tasks-hero mb-6">
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 class="hero-title text-white font-extrabold text-xl sm:text-2xl m-0">Mis Actividades Diarias</h2>
          <p class="hero-desc text-slate-300 text-xs sm:text-sm m-0 mt-1">
            Registra y gestiona tu bitácora de tareas, avances y entregables diarios.
          </p>
        </div>
        <div class="w-full sm:w-auto">
          <vs-button
            @click="abrirModalNuevaTarea"
            color="success"
            type="filled"
            icon="add"
            class="w-full sm:w-auto shadow-lg font-bold px-5 py-3"
          >
            + Registrar Nueva Tarea
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Metrics Summary Cards (Responsive Grid) -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
      <div class="stat-card stat-card--blue">
        <div class="stat-icon bg-blue-100 text-blue-600">
          <i class="feather icon-list"></i>
        </div>
        <div class="stat-info">
          <span class="stat-label">Total Registradas</span>
          <h3 class="stat-value">{{ tareasFiltradas.length }}</h3>
        </div>
      </div>

      <div class="stat-card stat-card--amber">
        <div class="stat-icon bg-amber-100 text-amber-600">
          <i class="feather icon-clock"></i>
        </div>
        <div class="stat-info">
          <span class="stat-label">Pendientes</span>
          <h3 class="stat-value text-amber-600">{{ contarPorEstado('pendiente') }}</h3>
        </div>
      </div>

      <div class="stat-card stat-card--emerald">
        <div class="stat-icon bg-emerald-100 text-emerald-600">
          <i class="feather icon-check-circle"></i>
        </div>
        <div class="stat-info">
          <span class="stat-label">Validadas</span>
          <h3 class="stat-value text-emerald-600">{{ contarPorEstado('validada') }}</h3>
        </div>
      </div>

      <div class="stat-card stat-card--rose">
        <div class="stat-icon bg-rose-100 text-rose-600">
          <i class="feather icon-alert-circle"></i>
        </div>
        <div class="stat-info">
          <span class="stat-label">Rechazadas</span>
          <h3 class="stat-value text-rose-600">{{ contarPorEstado('rechazada') }}</h3>
        </div>
      </div>
    </div>

    <!-- Main Card -->
    <vx-card class="shadow-md">
      <!-- Toolbar & Filters -->
      <div class="filters-bar p-3 sm:p-4 mb-5 rounded-xl bg-slate-50 border border-slate-200/80">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4 items-end">
          
          <!-- Filtro Fecha -->
          <div class="lg:col-span-3">
            <label class="filter-label"><i class="feather icon-calendar mr-1"></i> Filtrar Fecha</label>
            <flat-pickr
              v-model="filtros.fecha"
              :config="configdateTimePicker"
              placeholder="Todas las fechas"
              class="w-full custom-input"
            />
          </div>

          <!-- Filtro Categoría (Filtradas por Departamento) -->
          <div class="lg:col-span-4">
            <div class="flex items-center justify-between mb-1">
              <label class="filter-label m-0"><i class="feather icon-tag mr-1"></i> Categoría del Área</label>
              <span class="text-xs text-primary font-semibold" v-if="nombreDepartamento">
                ({{ categoriasDisponibles.length }} en {{ nombreDepartamento }})
              </span>
            </div>
            <v-select
              v-model="filtros.categoria"
              :options="categoriasDisponibles"
              label="nombre_categoria"
              placeholder="Todas las categorías..."
              class="custom-vselect"
            >
              <template #option="option">
                <div class="flex items-center gap-2 py-1">
                  <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: option.color || '#3b82f6' }"></span>
                  <span class="font-medium text-slate-800 text-sm">{{ option.nombre_categoria }}</span>
                </div>
              </template>
              <template #selected-option="option">
                <div class="flex items-center gap-2">
                  <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ backgroundColor: option.color || '#3b82f6' }"></span>
                  <span>{{ option.nombre_categoria }}</span>
                </div>
              </template>
            </v-select>
          </div>

          <!-- Filtro Estado -->
          <div class="lg:col-span-3">
            <label class="filter-label"><i class="feather icon-filter mr-1"></i> Estado</label>
            <select v-model="filtros.estado" class="custom-input select-native">
              <option :value="null">Todos los estados</option>
              <option value="pendiente">Pendiente</option>
              <option value="validada">Validada</option>
              <option value="rechazada">Rechazada</option>
            </select>
          </div>

          <!-- Botones de Acción -->
          <div class="lg:col-span-2 flex gap-2 justify-start sm:justify-end">
            <vx-tooltip text="Limpiar filtros" v-if="filtros.fecha || filtros.categoria || filtros.estado">
              <vs-button @click="limpiarFiltros" color="dark" type="border" icon="clear" size="small">
                Limpiar
              </vs-button>
            </vx-tooltip>

            <vx-tooltip text="Recargar mis actividades">
              <vs-button @click="cargarTareas" color="primary" type="border" icon="refresh" size="small" :class="{ 'spin-icon': cargando }">
                Recargar
              </vs-button>
            </vx-tooltip>
          </div>
        </div>
      </div>

      <!-- Buscador rápido y contador -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 px-1">
        <div class="flex items-center gap-2">
          <span class="font-bold text-slate-800 text-sm sm:text-base">Bitácora de Tareas</span>
          <vs-chip color="primary" class="font-bold text-xs">{{ tareasFiltradas.length }} items</vs-chip>
        </div>
        <div class="w-full sm:w-64">
          <vs-input
            v-model="busquedaTexto"
            placeholder="Buscar por título, desc..."
            icon="search"
            size="small"
            class="w-full"
          />
        </div>
      </div>

      <!-- Contenedor Responsive con Horizontal Scroll -->
      <div class="table-responsive-wrapper overflow-x-auto">
        <vs-table :data="tareasMostradas" stripe class="table-styled min-w-[700px]">
          <template slot="thead">
            <vs-th>Fecha</vs-th>
            <vs-th>Categoría</vs-th>
            <vs-th>Título & Descripción</vs-th>
            <vs-th class="text-center">Cant / Ref</vs-th>
            <vs-th>Estado</vs-th>
            <vs-th>Validaciones</vs-th>
            <vs-th class="text-center">Acciones</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr v-for="(tarea, index) in data" :key="index" class="hover:bg-slate-50 transition-colors">
              <!-- Fecha -->
              <vs-td class="whitespace-nowrap font-medium text-slate-700">
                <div class="flex items-center gap-1.5">
                  <i class="feather icon-calendar text-blue-500"></i>
                  <span>{{ formatDate(tarea.fecha_tarea) }}</span>
                </div>
              </vs-td>

              <!-- Categoría -->
              <vs-td>
                <div v-if="tarea.categoria" class="category-chip" :style="{ backgroundColor: getBgLight(tarea.categoria.color) }">
                  <span class="chip-dot" :style="{ backgroundColor: tarea.categoria.color || '#3b82f6' }"></span>
                  <span class="chip-label" :style="{ color: tarea.categoria.color || '#1e293b' }">{{ tarea.categoria.nombre_categoria }}</span>
                </div>
                <span v-else class="text-slate-400 italic text-xs">Sin categoría</span>
              </vs-td>

              <!-- Título y Descripción -->
              <vs-td>
                <div class="task-title-col">
                  <h5 class="font-bold text-slate-800 text-sm m-0">{{ tarea.titulo_tarea }}</h5>
                  <p class="text-xs text-slate-500 m-0 line-clamp-1" :title="tarea.descripcion_tarea">
                    {{ tarea.descripcion_tarea }}
                  </p>
                </div>
              </vs-td>

              <!-- Cantidad & Referencia -->
              <vs-td class="text-center">
                <div class="flex flex-col items-center">
                  <span class="font-extrabold text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full text-xs border border-blue-200">
                    {{ tarea.cantidad }}
                  </span>
                  <span v-if="tarea.referencia_libro" class="text-[10px] text-slate-400 mt-0.5" :title="tarea.referencia_libro">
                    {{ tarea.referencia_libro }}
                  </span>
                </div>
              </vs-td>

              <!-- Estado -->
              <vs-td>
                <span class="status-pill" :class="`status-pill--${tarea.estado}`">
                  <i :class="getEstadoIcon(tarea.estado)" class="mr-1"></i>
                  {{ getEstadoTexto(tarea.estado) }}
                </span>
              </vs-td>

              <!-- Validaciones -->
              <vs-td>
                <div class="flex gap-1.5 items-center">
                  <vx-tooltip
                    v-for="val in (tarea.validaciones || [])"
                    :key="val.id_validacion"
                    :text="`${val.validador ? val.validador.nombres : 'Validador'}: ${val.validado ? 'Aprobado' : 'Observado'}`"
                  >
                    <span
                      class="validation-badge"
                      :class="val.validado ? 'validation-badge--ok' : 'validation-badge--fail'"
                    >
                      <i :class="val.validado ? 'feather icon-check' : 'feather icon-x'"></i>
                    </span>
                  </vx-tooltip>
                  <span v-if="!(tarea.validaciones && tarea.validaciones.length)" class="text-xs text-slate-400 italic">
                    En espera
                  </span>
                </div>
              </vs-td>

              <!-- Acciones con espaciado amplio -->
              <vs-td>
                <div class="flex justify-center items-center gap-2">
                  <vx-tooltip text="Ver detalle completo">
                    <vs-button
                      size="small"
                      icon="visibility"
                      @click="verDetalleTarea(tarea)"
                      color="primary"
                      type="flat"
                      class="action-btn"
                    />
                  </vx-tooltip>

                  <vx-tooltip v-if="tarea.estado === 'pendiente' || userRoot" text="Editar actividad">
                    <vs-button
                      size="small"
                      icon="edit"
                      @click="editarTarea(tarea)"
                      color="warning"
                      type="flat"
                      class="action-btn"
                    />
                  </vx-tooltip>

                  <vx-tooltip v-if="tarea.estado === 'pendiente' || userRoot" text="Eliminar actividad">
                    <vs-button
                      size="small"
                      icon="delete"
                      @click="eliminarTarea(tarea.id_tarea)"
                      color="danger"
                      type="flat"
                      class="action-btn"
                    />
                  </vx-tooltip>
                </div>
              </vs-td>
            </vs-tr>

            <div v-if="!tareasMostradas.length" class="text-center py-10 text-slate-400">
              <i class="feather icon-inbox text-4xl block mb-2 opacity-50"></i>
              <p class="text-sm font-medium m-0">No se encontraron tareas con los filtros seleccionados.</p>
            </div>
          </template>
        </vs-table>
      </div>
    </vx-card>

    <!-- MODAL REGISTRAR / EDITAR TAREA -->
    <vs-popup
      :title="tareaForm.id_tarea ? 'Editar Actividad' : 'Registrar Nueva Actividad'"
      :active.sync="modalTarea"
    >
      <div class="modal-form-content p-2">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          
          <!-- Fecha -->
          <div>
            <label class="form-label">Fecha de Realización *</label>
            <flat-pickr
              v-model="tareaForm.fecha_tarea"
              :config="configdateTimePicker"
              placeholder="Seleccionar fecha"
              class="w-full custom-input"
            />
          </div>

          <!-- Categoría del área -->
          <div>
            <div class="flex justify-between items-center mb-1">
              <label class="form-label m-0">Categoría del Área *</label>
              <span class="text-xs text-slate-400 font-normal" v-if="nombreDepartamento">
                {{ nombreDepartamento }}
              </span>
            </div>
            <v-select
              v-model="tareaForm.categoria"
              :options="categoriasDisponibles"
              label="nombre_categoria"
              placeholder="Selecciona la categoría..."
              class="custom-vselect"
            >
              <template #option="option">
                <div class="flex items-center gap-2 py-1">
                  <span class="w-3.5 h-3.5 rounded-full flex-shrink-0" :style="{ backgroundColor: option.color || '#3b82f6' }"></span>
                  <span class="font-medium text-slate-800 text-sm">{{ option.nombre_categoria }}</span>
                </div>
              </template>
              <template #selected-option="option">
                <div class="flex items-center gap-2">
                  <span class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: option.color || '#3b82f6' }"></span>
                  <span class="font-semibold">{{ option.nombre_categoria }}</span>
                </div>
              </template>
            </v-select>
          </div>

          <!-- Título -->
          <div class="md:col-span-2">
            <label class="form-label">Título de la Actividad *</label>
            <vs-input
              v-model="tareaForm.titulo_tarea"
              class="w-full"
              placeholder="Ej: Diagramación de capítulo 3 o Desarrollo de API de reportes"
            />
          </div>

          <!-- Descripción -->
          <div class="md:col-span-2">
            <label class="form-label">Descripción Detallada *</label>
            <vs-textarea
              v-model="tareaForm.descripcion_tarea"
              class="w-full"
              rows="3"
              placeholder="Describe detalladamente el trabajo realizado, entregables o avances..."
            />
          </div>

          <!-- Referencia Libro -->
          <div>
            <label class="form-label">Referencia / Proyecto / Libro</label>
            <vs-input
              v-model="tareaForm.referencia_libro"
              class="w-full"
              placeholder="Ej: Matemáticas 6to EGB o Módulo Facturación"
            />
          </div>

          <!-- Referencia Página -->
          <div>
            <label class="form-label">Detalle / Página / Entregable</label>
            <vs-input
              v-model="tareaForm.referencia_pagina"
              class="w-full"
              placeholder="Ej: Págs 45-50 o Sprint 2"
            />
          </div>

          <!-- Cantidad -->
          <div>
            <label class="form-label">Cantidad / Unidades Realizadas *</label>
            <vs-input-number
              v-model="tareaForm.cantidad"
              :min="1"
              class="w-full"
            />
          </div>
        </div>

        <div class="flex justify-end gap-3 border-t pt-4 mt-4">
          <vs-button @click="modalTarea = false" color="danger" type="flat">
            Cancelar
          </vs-button>
          <vs-button @click="guardarTarea" color="primary" class="font-bold px-6">
            <i class="feather icon-save mr-1"></i>
            {{ tareaForm.id_tarea ? 'Actualizar Actividad' : 'Guardar Actividad' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- MODAL DETALLE TAREA -->
    <vs-popup
      title="Detalle de la Actividad"
      :active.sync="modalDetalle"
    >
      <div v-if="tareaSeleccionada" class="p-4 space-y-4">
        <div class="flex items-center justify-between border-b pb-3">
          <div>
            <span class="text-xs text-slate-400 font-semibold uppercase">Fecha</span>
            <h5 class="font-bold text-slate-800 m-0">{{ formatDate(tareaSeleccionada.fecha_tarea) }}</h5>
          </div>
          <div>
            <span class="status-pill" :class="`status-pill--${tareaSeleccionada.estado}`">
              {{ getEstadoTexto(tareaSeleccionada.estado) }}
            </span>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border">
          <div>
            <span class="text-xs text-slate-400 font-semibold block">Categoría</span>
            <span class="font-bold text-slate-800" v-if="tareaSeleccionada.categoria">
              {{ tareaSeleccionada.categoria.nombre_categoria }}
            </span>
            <span v-else class="text-slate-400 italic">Sin categoría</span>
          </div>
          <div>
            <span class="text-xs text-slate-400 font-semibold block">Cantidad</span>
            <span class="font-bold text-blue-600">{{ tareaSeleccionada.cantidad }} unidad(es)</span>
          </div>
        </div>

        <div>
          <span class="text-xs text-slate-400 font-semibold block mb-1">Título de la Actividad</span>
          <h4 class="font-bold text-slate-900 m-0 text-base">{{ tareaSeleccionada.titulo_tarea }}</h4>
        </div>

        <div>
          <span class="text-xs text-slate-400 font-semibold block mb-1">Descripción</span>
          <p class="text-sm text-slate-700 bg-white p-3 rounded border whitespace-pre-line m-0">
            {{ tareaSeleccionada.descripcion_tarea }}
          </p>
        </div>

        <div v-if="tareaSeleccionada.referencia_libro || tareaSeleccionada.referencia_pagina" class="grid grid-cols-2 gap-3 text-sm">
          <div v-if="tareaSeleccionada.referencia_libro">
            <span class="text-xs text-slate-400 font-semibold block">Referencia / Libro</span>
            <span class="font-medium text-slate-800">{{ tareaSeleccionada.referencia_libro }}</span>
          </div>
          <div v-if="tareaSeleccionada.referencia_pagina">
            <span class="text-xs text-slate-400 font-semibold block">Detalle / Página</span>
            <span class="font-medium text-slate-800">{{ tareaSeleccionada.referencia_pagina }}</span>
          </div>
        </div>

        <!-- Validaciones -->
        <div v-if="tareaSeleccionada.validaciones && tareaSeleccionada.validaciones.length" class="border-t pt-3">
          <h5 class="font-bold text-slate-800 mb-2 text-sm">Historial de Validaciones</h5>
          <div
            v-for="val in tareaSeleccionada.validaciones"
            :key="val.id_validacion"
            class="p-2.5 rounded-lg border mb-2"
            :class="val.validado ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'"
          >
            <div class="flex items-center justify-between text-xs font-semibold">
              <span :class="val.validado ? 'text-emerald-700' : 'text-rose-700'">
                <i :class="val.validado ? 'feather icon-check-circle' : 'feather icon-x-circle'" class="mr-1"></i>
                {{ val.validador ? val.validador.nombres : 'Validador' }} ({{ val.tipo_validador }})
              </span>
              <span class="text-slate-400">{{ val.created_at ? formatDate(val.created_at) : '' }}</span>
            </div>
            <p v-if="val.comentario" class="text-xs text-slate-600 mt-1 m-0">{{ val.comentario }}</p>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Spanish } from 'flatpickr/dist/l10n/es.js'
import vSelect from 'vue-select'
import { mapState } from 'vuex'

export default {
  name: 'RegistroTareasDiarias',
  components: {
    flatPickr,
    vSelect
  },
  data() {
    return {
      tareas: [],
      categoriasDisponibles: [],
      nombreDepartamento: '',
      cargando: false,
      busquedaTexto: '',
      filtros: {
        fecha: null,
        categoria: null,
        estado: null
      },
      modalTarea: false,
      modalDetalle: false,
      tareaSeleccionada: null,
      tareaForm: {
        id_tarea: null,
        fecha_tarea: new Date().toISOString().split('T')[0],
        categoria: null,
        titulo_tarea: '',
        descripcion_tarea: '',
        referencia_libro: '',
        referencia_pagina: '',
        cantidad: 1
      },
      configdateTimePicker: {
        locale: Spanish,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd/m/Y'
      }
    }
  },
  computed: {
    ...mapState(['auth', 'userRoot', 'susuario']),
    usuarioId() {
      if (this.auth && this.auth.idusuario) return this.auth.idusuario
      const u = JSON.parse(localStorage.getItem('usuario') || '{}')
      return u.idusuario || null
    },
    usuarioDepartamentoId() {
      const u = JSON.parse(localStorage.getItem('usuario') || '{}')
      return u.tdp_id || (this.susuario ? this.susuario.tdp_id : null)
    },
    tareasFiltradas() {
      let resultado = this.tareas

      if (this.filtros.fecha) {
        resultado = resultado.filter(t => (t.fecha_tarea || '').substring(0, 10) === this.filtros.fecha)
      }

      if (this.filtros.categoria) {
        resultado = resultado.filter(t => t.id_categoria === this.filtros.categoria.id_categoria)
      }

      if (this.filtros.estado) {
        resultado = resultado.filter(t => t.estado === this.filtros.estado)
      }

      return resultado
    },
    tareasMostradas() {
      const q = this.busquedaTexto.toLowerCase().trim()
      if (!q) return this.tareasFiltradas
      return this.tareasFiltradas.filter(t =>
        (t.titulo_tarea || '').toLowerCase().includes(q) ||
        (t.descripcion_tarea || '').toLowerCase().includes(q) ||
        (t.referencia_libro || '').toLowerCase().includes(q)
      )
    }
  },
  mounted() {
    this.cargarCategorias()
    this.cargarTareas()
  },
  methods: {
    async cargarCategorias() {
      try {
        const usuario = JSON.parse(localStorage.getItem('usuario') || '{}')
        const tdpId = usuario.tdp_id || (this.susuario ? this.susuario.tdp_id : null)

        let url = this.$server_url + 'actividades-control/categorias'
        // Si el usuario pertenece a un departamento, solicitar únicamente las categorías de su área
        if (tdpId && !this.userRoot) {
          url += '/' + tdpId
        }

        const response = await this.$http.get(url)
        this.categoriasDisponibles = response.data || []

        // Obtener nombre del departamento
        if (this.categoriasDisponibles.length && this.categoriasDisponibles[0].departamento) {
          this.nombreDepartamento = this.categoriasDisponibles[0].departamento.tdp_nombre
        } else if (usuario.departamento_nombre) {
          this.nombreDepartamento = usuario.departamento_nombre
        }
      } catch (error) {
        console.error('Error al cargar categorías:', error)
      }
    },
    async cargarTareas() {
      if (!this.usuarioId) return
      this.cargando = true
      this.$vs.loading()
      try {
        const response = await this.$http.get(this.$server_url + 'actividades-control/tareas', {
          params: { idusuario: this.usuarioId }
        })
        this.tareas = response.data || []
      } catch (error) {
        console.error('Error al cargar tareas:', error)
        this.$vs.notify({ title: 'Error', text: 'No se pudieron cargar las tareas', color: 'danger' })
      } finally {
        this.cargando = false
        this.$vs.loading.close()
      }
    },
    limpiarFiltros() {
      this.filtros.fecha = null
      this.filtros.categoria = null
      this.filtros.estado = null
      this.busquedaTexto = ''
    },
    abrirModalNuevaTarea() {
      this.tareaForm = {
        id_tarea: null,
        fecha_tarea: new Date().toISOString().split('T')[0],
        categoria: this.categoriasDisponibles.length ? this.categoriasDisponibles[0] : null,
        titulo_tarea: '',
        descripcion_tarea: '',
        referencia_libro: '',
        referencia_pagina: '',
        cantidad: 1
      }
      this.modalTarea = true
    },
    editarTarea(tarea) {
      this.tareaForm = {
        id_tarea: tarea.id_tarea,
        fecha_tarea: (tarea.fecha_tarea || '').substring(0, 10),
        categoria: tarea.categoria,
        titulo_tarea: tarea.titulo_tarea,
        descripcion_tarea: tarea.descripcion_tarea,
        referencia_libro: tarea.referencia_libro || '',
        referencia_pagina: tarea.referencia_pagina || '',
        cantidad: tarea.cantidad
      }
      this.modalTarea = true
    },
    async guardarTarea() {
      if (!this.tareaForm.categoria || !this.tareaForm.titulo_tarea || !this.tareaForm.descripcion_tarea) {
        this.$vs.notify({
          title: 'Atención',
          text: 'Complete la categoría, título y descripción obligatorios',
          color: 'warning'
        })
        return
      }

      this.$vs.loading()
      try {
        const payload = {
          idusuario: this.usuarioId,
          id_categoria: this.tareaForm.categoria.id_categoria,
          fecha_tarea: this.tareaForm.fecha_tarea,
          titulo_tarea: this.tareaForm.titulo_tarea,
          descripcion_tarea: this.tareaForm.descripcion_tarea,
          referencia_libro: this.tareaForm.referencia_libro,
          referencia_pagina: this.tareaForm.referencia_pagina,
          cantidad: this.tareaForm.cantidad
        }

        if (this.tareaForm.id_tarea) {
          await this.$http.put(this.$server_url + `actividades-control/tareas/${this.tareaForm.id_tarea}`, payload)
          this.$vs.notify({ title: '¡Actualizado!', text: 'Tarea modificada correctamente', color: 'success' })
        } else {
          await this.$http.post(this.$server_url + 'actividades-control/tareas', payload)
          this.$vs.notify({ title: '¡Registrada!', text: 'Nueva tarea agregada exitosamente', color: 'success' })
        }

        this.modalTarea = false
        this.cargarTareas()
      } catch (error) {
        console.error('Error al guardar tarea:', error)
        this.$vs.notify({ title: 'Error', text: 'No se pudo guardar la tarea', color: 'danger' })
      } finally {
        this.$vs.loading.close()
      }
    },
    async eliminarTarea(idTarea) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirmar eliminación',
        text: '¿Está seguro de eliminar esta actividad registrada?',
        accept: async () => {
          this.$vs.loading()
          try {
            await this.$http.delete(this.$server_url + `actividades-control/tareas/${idTarea}`)
            this.$vs.notify({ title: 'Eliminado', text: 'Actividad eliminada correctamente', color: 'success' })
            this.cargarTareas()
          } catch (error) {
            console.error('Error al eliminar tarea:', error)
            this.$vs.notify({ title: 'Error', text: 'No se pudo eliminar la tarea', color: 'danger' })
          } finally {
            this.$vs.loading.close()
          }
        }
      })
    },
    verDetalleTarea(tarea) {
      this.tareaSeleccionada = tarea
      this.modalDetalle = true
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },
    getEstadoColor(estado) {
      const colores = { pendiente: 'warning', en_revision: 'primary', validada: 'success', rechazada: 'danger' }
      return colores[estado] || 'dark'
    },
    getEstadoTexto(estado) {
      const textos = { pendiente: 'Pendiente', en_revision: 'En Revisión', validada: 'Validada', rechazada: 'Rechazada' }
      return textos[estado] || estado
    },
    getEstadoIcon(estado) {
      const icones = { pendiente: 'feather icon-clock', validada: 'feather icon-check-circle', rechazada: 'feather icon-x-circle' }
      return icones[estado] || 'feather icon-circle'
    },
    contarPorEstado(estado) {
      return this.tareasFiltradas.filter(t => t.estado === estado).length
    },
    getBgLight(hexColor) {
      if (!hexColor) return '#eff6ff'
      return hexColor + '18'
    }
  }
}
</script>

<style scoped>
.registro-tareas-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Badge de Departamento */
.dept-badge {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #334155;
  padding: 5px 14px;
  border-radius: 9999px;
  font-size: 0.8rem;
}

/* Hero Banner */
.tasks-hero {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 16px;
  padding: 1.5rem 2rem;
  color: white;
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.2);
}

/* Stat Cards */
.stat-card {
  background: white;
  padding: 1.1rem 1.25rem;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  transition: transform 0.2s;
}
.stat-card:hover { transform: translateY(-2px); }
.stat-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem; flex-shrink: 0;
}
.stat-info { display: flex; flex-direction: column; }
.stat-label { font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.03em; }
.stat-value { font-size: 1.5rem; font-weight: 800; color: #0f172a; margin: 0; line-height: 1; }

/* Filter Bar & Inputs */
.filter-label { font-size: 0.72rem; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.03em; display: block; margin-bottom: 4px; }
.custom-input {
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.86rem;
  color: #1e293b;
  outline: none;
  transition: border 0.15s;
}
.custom-input:focus { border-color: #3b82f6; }
.select-native { height: 38px; cursor: pointer; }

/* Category chip */
.category-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 700;
}
.chip-dot { width: 8px; height: 8px; border-radius: 50%; }

/* Status pill */
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.status-pill--pendiente { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
.status-pill--validada { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
.status-pill--rechazada { background: #fff1f2; color: #e11d48; border: 1px solid #fecdd3; }

/* Validation badge */
.validation-badge {
  width: 24px; height: 24px;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: bold;
}
.validation-badge--ok { background: #dcfce7; color: #16a34a; }
.validation-badge--fail { background: #ffe4e6; color: #e11d48; }

/* Action Buttons con espaciado cómodo */
.action-btn {
  margin: 0 2px !important;
  border-radius: 6px !important;
}

.form-label { font-size: 0.82rem; font-weight: 700; color: #334155; display: block; margin-bottom: 4px; }
.spin-icon i { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Table scroll responsive wrapper */
.table-responsive-wrapper {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}
</style>
